import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, ChevronDown, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  SyncHistoryEntry,
  getSyncHistory,
  triggerDistributorSync,
} from '../../services/distributorService';

/**
 * Sync history surface for a single distributor — KPI tiles (success rate,
 * last failure, records/sync) over the recent window, plus an activity feed
 * of the last N runs with per-row progress, status, and trigger source.
 *
 * Run now button + always-on Auto-sync indicator at the header. Auto-sync
 * is currently a system-managed scheduled trigger (every 6 hours) with no
 * per-partner kill switch — the toggle is shown for design parity but
 * disabled, with a tooltip explaining the gap.
 *
 * Honest deviations from the design comp:
 *  - "AVG DURATION" KPI omitted: backend tracks completion timestamp only,
 *    not start. Adding requires schema field + writer change; deferred.
 *  - Per-row duration column omitted for the same reason.
 *  - "View all N syncs" expands in-place rather than paginating server-side.
 */

interface SyncHistoryPanelProps {
  distributorId: string;
  distributorLabel: string;
}

const RECENT_WINDOW = 7;
const KPI_WINDOW_DAYS = 30;

export function SyncHistoryPanel({ distributorId, distributorLabel }: SyncHistoryPanelProps) {
  const [entries, setEntries] = useState<SyncHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSyncHistory();
      // Filter to this distributor — endpoint returns all sources cross-mixed.
      const filtered = (result.entries ?? []).filter((e) => e.source === distributorId);
      setEntries(filtered);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load sync history');
    } finally {
      setLoading(false);
    }
  }, [distributorId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunNow = async () => {
    setSyncing(true);
    setError(null);
    try {
      await triggerDistributorSync(distributorId);
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Run failed');
    } finally {
      setSyncing(false);
    }
  };

  const kpis = useMemo(() => {
    if (!entries || entries.length === 0) {
      return { successRatePct: null, lastFailureAt: null, avgRecords: null };
    }
    const cutoff = Date.now() - KPI_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const inWindow = entries.filter((e) => new Date(e.syncedAt).getTime() >= cutoff);
    const successCount = inWindow.filter((e) => e.success).length;
    const successRatePct = inWindow.length > 0
      ? Math.round((successCount / inWindow.length) * 1000) / 10
      : null;
    const lastFailure = entries.find((e) => !e.success);
    const totalRecords = entries.reduce((acc, e) => acc + (e.productsUpdated ?? 0), 0);
    const avgRecords = entries.length > 0 ? Math.round(totalRecords / entries.length) : null;
    return {
      successRatePct,
      lastFailureAt: lastFailure?.syncedAt ?? null,
      avgRecords,
    };
  }, [entries]);

  const visibleEntries = expanded ? entries ?? [] : (entries ?? []).slice(0, RECENT_WINDOW);

  return (
    <div className="px-6 pt-2 pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">Sync history</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Every {distributorLabel} pull · what changed in your catalogue · last 30 days
          </p>
        </div>
        <div className="inline-flex items-center gap-3 flex-shrink-0">
          <AutoSyncIndicator />
          <Button
            type="button"
            variant="outline"
            onClick={handleRunNow}
            disabled={syncing}
            className="inline-flex h-9"
          >
            {syncing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Syncing…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                Run now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <KpiTile
          label="Success rate (30d)"
          value={kpis.successRatePct !== null ? `${kpis.successRatePct}%` : '—'}
          dotTone={
            kpis.successRatePct === null
              ? null
              : kpis.successRatePct >= 99
              ? 'green'
              : kpis.successRatePct >= 95
              ? 'amber'
              : 'red'
          }
        />
        <KpiTile
          label="Last failure"
          value={kpis.lastFailureAt ? formatRelative(kpis.lastFailureAt) : 'Never'}
        />
        <KpiTile
          label="Records / sync"
          value={kpis.avgRecords !== null ? kpis.avgRecords.toLocaleString() : '—'}
        />
      </div>

      {/* Activity */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Activity{' '}
          <span className="font-normal text-slate-500">
            · last {Math.min(RECENT_WINDOW, entries?.length ?? 0)} sync
            {(entries?.length ?? 0) === 1 ? '' : 's'}
          </span>
        </h3>
        <span className="inline-flex items-center text-xs text-slate-500">
          ▾ {distributorLabel}
        </span>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sync history…
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <p className="text-sm text-slate-700 font-medium">No syncs yet</p>
          <p className="text-xs text-slate-500 mt-1">
            Click "Run now" to fetch live price &amp; stock for your seed SKUs.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {visibleEntries.map((entry) => (
              <ActivityRow
                key={entry.id}
                entry={entry}
                distributorLabel={distributorLabel}
              />
            ))}
          </div>
          {entries.length > RECENT_WINDOW && (
            <div className="text-center mt-3 text-xs text-slate-500">
              Showing {visibleEntries.length} of {entries.length} ·{' '}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-orange-600 hover:text-orange-700 underline"
              >
                {expanded ? 'Show less' : `View all ${entries.length} syncs`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── KPI tile ────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  dotTone,
}: {
  label: string;
  value: string;
  dotTone?: 'green' | 'amber' | 'red' | null;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1.5 text-3xl font-semibold text-slate-900 tabular-nums inline-flex items-center gap-2">
        {value}
        {dotTone && (
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              dotTone === 'green'
                ? 'bg-emerald-500'
                : dotTone === 'amber'
                ? 'bg-amber-500'
                : 'bg-rose-500'
            }`}
          />
        )}
      </div>
    </div>
  );
}

// ─── Auto-sync indicator (read-only stub) ────────────────────────────

function AutoSyncIndicator() {
  return (
    <div
      className="inline-flex items-center gap-2 text-xs text-slate-500"
      title="Auto-sync runs every 6 hours and is currently always-on. A per-partner kill switch will land in a future release."
    >
      Auto-sync
      <span className="inline-flex h-5 w-9 items-center rounded-full bg-emerald-500 p-0.5 cursor-not-allowed">
        <span className="inline-block h-4 w-4 rounded-full bg-white shadow-sm translate-x-4" />
      </span>
    </div>
  );
}

// ─── Activity row ────────────────────────────────────────────────────

function ActivityRow({
  entry,
  distributorLabel,
}: {
  entry: SyncHistoryEntry;
  distributorLabel: string;
}) {
  const updated = entry.productsUpdated ?? 0;
  const failed = entry.productsFailed ?? 0;
  const total = updated + failed;
  const successPct = total > 0 ? (updated / total) * 100 : 100;
  const status: 'success' | 'partial' | 'error' = !entry.success
    ? 'error'
    : failed > 0
    ? 'partial'
    : 'success';

  const priceChanges = entry.priceChanges ?? [];
  const stockChanges = entry.stockChanges ?? [];
  const hasDetail = priceChanges.length > 0 || stockChanges.length > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`grid grid-cols-[1.25rem_minmax(140px,180px)_minmax(140px,180px)_minmax(110px,120px)_1fr_5rem_1.25rem] items-center gap-3 px-4 py-3 ${
          hasDetail ? 'cursor-pointer hover:bg-slate-50/60' : ''
        }`}
      >
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            status === 'success'
              ? 'bg-emerald-500'
              : status === 'partial'
              ? 'bg-amber-500'
              : 'bg-rose-500'
          }`}
        />
        <div className="min-w-0">
          <div className="text-sm text-slate-900">
            {formatWhen(entry.syncedAt)}
          </div>
          <div className="text-[11px] text-slate-400 font-mono">
            sync-{shortId(entry.id)}
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <DistributorBadge distributorId={entry.source} />
          <span className="text-sm text-slate-700 truncate">{distributorLabel}</span>
        </div>
        <span
          className={`inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[11px] font-medium ${
            entry.triggerType === 'manual'
              ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200'
              : 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200'
          }`}
        >
          {entry.triggerType === 'manual' ? 'Manual' : 'Scheduled'}
        </span>
        <div className="min-w-0">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                status === 'error' ? 'bg-rose-400' : 'bg-orange-400'
              }`}
              style={{ width: `${Math.max(successPct, 4)}%` }}
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5 tabular-nums">
            <span className="text-slate-700">{updated.toLocaleString()}</span> updated
            {failed > 0 && (
              <>
                {' · '}
                <span className="text-rose-600">{failed} failed</span>
              </>
            )}
            {hasDetail && (
              <>
                {' · '}
                <span className="text-slate-500">
                  {priceChanges.length > 0 && `${priceChanges.length} price`}
                  {priceChanges.length > 0 && stockChanges.length > 0 && ', '}
                  {stockChanges.length > 0 && `${stockChanges.length} stock`}
                  {' '}change{priceChanges.length + stockChanges.length === 1 ? '' : 's'}
                </span>
              </>
            )}
            {!entry.success && entry.error && (
              <>
                {' · '}
                <span className="text-rose-600 truncate">{entry.error}</span>
              </>
            )}
          </div>
        </div>
        <StatusPill status={status} />
        <span className="text-slate-400">
          {hasDetail ? (
            expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          ) : null}
        </span>
      </div>
      {expanded && hasDetail && (
        <div className="px-4 pb-4 pt-1 bg-slate-50 border-t border-slate-100">
          <ChangeBreakdown priceChanges={priceChanges} stockChanges={stockChanges} />
        </div>
      )}
    </div>
  );
}

function ChangeBreakdown({
  priceChanges,
  stockChanges,
}: {
  priceChanges: Array<{ sku: string; productName: string; oldBuyPrice: number; newBuyPrice: number }>;
  stockChanges: Array<{ sku: string; productName: string; oldSoh: number | null; newSoh: number | null }>;
}) {
  const PER_KIND_CAP = 8;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Price changes ({priceChanges.length})
        </div>
        {priceChanges.length === 0 ? (
          <p className="text-xs text-slate-400 italic">None</p>
        ) : (
          <ul className="space-y-1.5">
            {priceChanges.slice(0, PER_KIND_CAP).map((c) => {
              const delta = c.newBuyPrice - c.oldBuyPrice;
              const up = delta > 0;
              return (
                <li key={c.sku} className="text-xs grid grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="min-w-0">
                    <div className="font-mono text-slate-700 truncate">{c.sku}</div>
                    <div className="text-slate-500 text-[11px] truncate">{c.productName}</div>
                  </div>
                  <div className={`tabular-nums inline-flex items-center gap-1 ${up ? 'text-rose-600' : 'text-emerald-700'}`}>
                    {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    ${c.oldBuyPrice.toFixed(2)} → <span className="font-semibold">${c.newBuyPrice.toFixed(2)}</span>
                  </div>
                </li>
              );
            })}
            {priceChanges.length > PER_KIND_CAP && (
              <li className="text-[11px] text-slate-400 italic">
                + {priceChanges.length - PER_KIND_CAP} more
              </li>
            )}
          </ul>
        )}
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Stock changes ({stockChanges.length})
        </div>
        {stockChanges.length === 0 ? (
          <p className="text-xs text-slate-400 italic">None</p>
        ) : (
          <ul className="space-y-1.5">
            {stockChanges.slice(0, PER_KIND_CAP).map((c) => {
              const oldS = c.oldSoh ?? 0;
              const newS = c.newSoh ?? 0;
              const delta = newS - oldS;
              const up = delta > 0;
              return (
                <li key={c.sku} className="text-xs grid grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="min-w-0">
                    <div className="font-mono text-slate-700 truncate">{c.sku}</div>
                    <div className="text-slate-500 text-[11px] truncate">{c.productName}</div>
                  </div>
                  <div className={`tabular-nums inline-flex items-center gap-1 ${up ? 'text-emerald-700' : 'text-rose-600'}`}>
                    {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {oldS.toLocaleString()} → <span className="font-semibold">{newS.toLocaleString()}</span>
                  </div>
                </li>
              );
            })}
            {stockChanges.length > PER_KIND_CAP && (
              <li className="text-[11px] text-slate-400 italic">
                + {stockChanges.length - PER_KIND_CAP} more
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: 'success' | 'partial' | 'error' }) {
  const config = {
    success: { label: 'Success', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    partial: { label: 'Partial',  cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
    error:   { label: 'Error',    cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${config.cls}`}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-60" />
      {config.label}
    </span>
  );
}

function DistributorBadge({ distributorId }: { distributorId: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    ingram: { label: 'IM', cls: 'bg-blue-500 text-white' },
    dickerdata: { label: 'DD', cls: 'bg-cyan-600 text-white' },
    telstra: { label: 'TE', cls: 'bg-violet-500 text-white' },
  };
  const cfg = map[distributorId] ?? { label: '??', cls: 'bg-slate-500 text-white' };
  return (
    <span className={`inline-flex items-center justify-center h-6 w-6 rounded text-[10px] font-bold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ─── helpers ────────────────────────────────────────────────────────

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const t = d.getTime();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  if (t >= todayStart) return `Today · ${time}`;
  if (t >= yesterdayStart) return `Yesterday · ${time}`;
  const days = Math.floor((todayStart - t) / (24 * 60 * 60 * 1000));
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago · ${time}`;
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function shortId(id: string): string {
  // GUID → last 4 hex chars as a stable short form. Pure UI affordance.
  return id.replace(/-/g, '').slice(-4);
}
